import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useMemberAuth } from '../../hooks/useMemberAuth';
import { Menu, X, LayoutDashboard, Eye, User, LogOut } from 'lucide-react';

const navItems = [
  { to: '/associado/dashboard', label: 'Início', icon: LayoutDashboard },
  { to: '/transparencia', label: 'Transparência', icon: Eye, external: true },
  { to: '/associado/perfil', label: 'Meu Perfil', icon: User },
];

const MemberLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { member, logout } = useMemberAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/associado/login');
  };

  return (
    <div className="min-h-screen bg-brand-cream flex">
      {/* Sidebar overlay (mobile) */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-30 w-64 bg-brand-brown flex flex-col
          transform transition-transform duration-300 ease-in-out
          lg:relative lg:translate-x-0
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Sidebar header */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-brand-brown-dark">
          <Link to="/associado/dashboard" className="flex items-center space-x-3">
            <img
              src="/assets/logo-branco.png"
              alt="Ilê Asé Sòpònnón"
              className="h-10 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </Link>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden text-brand-cream hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Member info */}
        <div className="px-6 py-4 border-b border-brand-brown-dark">
          <div className="flex items-center space-x-3">
            {member?.photo_url ? (
              <img
                src={member.photo_url}
                alt={member.name}
                className="h-12 w-12 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="h-12 w-12 rounded-full bg-brand-gold flex items-center justify-center flex-shrink-0">
                <span className="text-white font-display font-bold text-lg">
                  {member?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-brand-beige text-xs uppercase tracking-wider">Associado</p>
              <p className="text-brand-cream font-semibold truncate">{member?.name}</p>
              {member?.registration_number && (
                <p className="text-brand-beige text-xs truncate">{member.registration_number}</p>
              )}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 py-6 space-y-1">
          {navItems.map(({ to, label, icon: Icon, external }) => {
            const isActive = !external && location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                onClick={() => setIsSidebarOpen(false)}
                className={`
                  flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors
                  ${isActive
                    ? 'bg-brand-gold text-brand-brown'
                    : 'text-brand-beige hover:bg-brand-brown-dark hover:text-brand-cream'
                  }
                `}
              >
                <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="px-4 py-4 border-t border-brand-brown-dark">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium text-brand-beige hover:bg-brand-brown-dark hover:text-brand-cream transition-colors"
          >
            <LogOut className="h-5 w-5 mr-3 flex-shrink-0" />
            Sair
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b-2 border-brand-beige h-20 flex items-center px-4 lg:px-8 shadow-sm">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-brand-cream mr-4"
          >
            <Menu className="h-6 w-6 text-brand-brown" />
          </button>
          <h1 className="font-display text-2xl font-bold text-brand-brown">
            Portal do Associado
          </h1>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MemberLayout;
